import { noop } from 'lodash-es';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useToggle } from 'react-use';
import { BoostIconDisplay } from 'src/components/SponsoredListings/BoostIconDisplay/BoostIconDisplay';
import { useAdCampaignsDataContext } from 'src/contexts/AdCampaignsDataContext';
import { useEventHubContext } from 'src/contexts/EventHubContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { useGetCompListings } from 'src/hooks/useGetCompListings';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useUserHasConsignorMasterUI } from 'src/hooks/useUserIsConsignorMasterUI';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import {
  DashboardIcon,
  DeliveryIcon,
  FlashSaleIcon,
  PlusIcon,
  PurchaseIcon,
  SellThroughRateIcon,
  TagNewIcon,
} from 'src/svgs';
import { SaleIcon } from 'src/svgs/SaleIcon';
import { PosIconProps } from 'src/svgs/SvgWrapper';
import { SearchSolidIcon } from 'src/svgs/Viagogo';
import { DemandDashboardIsOnSaleEventQueryParam } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import {
  CompListing,
  Event,
  EventWithData,
  Feature,
  Listing,
  SellerEventMarketplaceEventInfo,
  UserSetting,
} from 'src/WebApiController';

import { InventoryEventTabs } from '../InventoryEventTabs';
import { useMarketplaceEventInfo } from './InventoryEventLocalContext.hooks';

type EventContent = {
  id: InventoryEventTabs;
  Icon: React.ComponentType<Omit<PosIconProps, 'viewBox'>>;
  contentId: ContentId;
  useStroke?: boolean;
  mobileOnly?: boolean;
  hide?: boolean;
};

export type IInventoryEventLocalContext = {
  defaultTab?: InventoryEventTabs;
  activeTab: InventoryEventTabs;
  eventContents: EventContent[];
  marketplaceEventInfo?: SellerEventMarketplaceEventInfo[];
  listings: Listing[];
  compListings: CompListing[];
  showMap: boolean;
  toggleShowMap: (showMap: boolean) => void;
  setActiveTab: (tab: InventoryEventTabs) => void;
  refetchMarketplaceEventInfo: () => void;
  onUpdateDefaultType: (type: InventoryEventTabs) => void;
};

export const InventoryEventLocalContext =
  createContext<IInventoryEventLocalContext>({
    activeTab: InventoryEventTabs.Info,
    eventContents: [],
    listings: [],
    compListings: [],
    showMap: true,
    toggleShowMap: noop,
    setActiveTab: noop,
    refetchMarketplaceEventInfo: noop,
    onUpdateDefaultType: noop,
  });

export const useInventoryEventLocalContext = () =>
  useContext(InventoryEventLocalContext);

/**
 * Adds a modal to the page where the provider is added
 * Provides utils to set all or update part of the modal
 * The modal is assumed to be open when it contains content
 */
export const InventoryEventLocalContextProvider = ({
  children,
  event,
  accordionItemProps,
}: PropsWithChildren & {
  event?: Event;
  accordionItemProps?: EventWithData;
}) => {
  const hasMobileInventoryPageFeature = useUserHasFeature(
    Feature.MobileInventoryEventPage
  );
  const hasEventDiscoveryInInventoryViewFeature = useUserHasFeature(
    Feature.EventDiscoveryInInventoryView
  );

  const isMobileLayout =
    useMatchMedia('mobile') && hasMobileInventoryPageFeature;
  const [searchParams] = useSearchParams();
  const isFromEventDiscovery = useMemo(
    () => Boolean(searchParams.get(DemandDashboardIsOnSaleEventQueryParam)),
    [searchParams]
  );

  const contentTypeInitRef = useRef<boolean>(false);

  // Move the initial tab calculation into useState's lazy initialization
  const [activeTab, setActiveTab] = useState<InventoryEventTabs>(
    InventoryEventTabs.Info
  );

  // Used for mobile App
  const [showMap, toggleShowMap] = useToggle(true);

  const hasPricingV2DemandDashboard = useUserHasFeature(
    Feature.PricingV2DemandDashboard
  );
  const hasMobileInventoryEventPageFeature = useUserHasFeature(
    Feature.MobileInventoryEventPage
  );
  const hasConsignorMasterUI = useUserHasConsignorMasterUI();
  const hasSponsoredListingsV2 = useUserHasFeature(Feature.SponsoredListingsV2);
  const hasBoostTabFeature = useUserHasFeature(
    Feature.SponsoredListingsV2_EventDetailBoostTab
  );
  const { allCampaignData, isEventsEnabledForSponsoredListingsLoading } =
    useAdCampaignsDataContext();

  const thisEventCampaigns = useMemo(() => {
    if (
      event?.viagId == null ||
      !allCampaignData ||
      !isEventsEnabledForSponsoredListingsLoading(event.viagId)
    ) {
      return null;
    }
    return allCampaignData[event.viagId] || null;
  }, [
    allCampaignData,
    event?.viagId,
    isEventsEnabledForSponsoredListingsLoading,
  ]);

  const hasPricingABTestFeature = useUserHasFeature(Feature.PricingABTest);
  const hasFulfillmentRulesEventLevelFeature = useUserHasFeature(
    Feature.FulfillmentRulesEventLevel
  );
  const hasAutoPricingFeature = useUserHasFeature(Feature.AutoPricing);

  const { marketplaceEventInfo, refetchMarketplaceEventInfo } =
    useMarketplaceEventInfo(event);

  const {
    value: defaultInventoryTabUserSetting,
    setUserSetting: setDefaultInventoryTabUserSetting,
  } = useServerUserSetting<InventoryEventTabs>({
    id: UserSetting.InventoryDefaultTab,
  });

  const { onUpdateInventoryTab, isOnSidePanel, inventoryTab, enabled } =
    useEventHubContext();

  const listings = useMemo(
    () => accordionItemProps?.entities?.listings ?? [],
    [accordionItemProps?.entities?.listings]
  );
  const { compListingsQuery } = useGetCompListings(event);

  const onSetInventoryTab = useCallback(
    (tab: InventoryEventTabs) => {
      setActiveTab(tab);
      if (enabled && !isOnSidePanel) {
        onUpdateInventoryTab(tab);
      }
    },
    [enabled, isOnSidePanel, onUpdateInventoryTab]
  );

  useEffect(() => {
    if (isOnSidePanel && inventoryTab) {
      setActiveTab(inventoryTab);
    }
  }, [enabled, inventoryTab, isOnSidePanel]);

  useEffect(() => {
    if (isFromEventDiscovery) {
      setActiveTab(InventoryEventTabs.EventDiscovery);
      return;
    }
    if (
      !isMobileLayout &&
      defaultInventoryTabUserSetting &&
      contentTypeInitRef.current === false &&
      Object.values(InventoryEventTabs).includes(defaultInventoryTabUserSetting)
    ) {
      if (defaultInventoryTabUserSetting === InventoryEventTabs.None) {
        setActiveTab(InventoryEventTabs.Info);
      } else {
        setActiveTab(defaultInventoryTabUserSetting);
      }
      contentTypeInitRef.current = true;
    }
  }, [defaultInventoryTabUserSetting, isFromEventDiscovery, isMobileLayout]);

  const eventContents = useMemo(() => {
    const contents: EventContent[] = [
      {
        id: InventoryEventTabs.Info,
        Icon: DashboardIcon,
        contentId: ContentId.Inventory,
      },
    ];

    if (hasEventDiscoveryInInventoryViewFeature && !isMobileLayout) {
      contents.push({
        id: InventoryEventTabs.EventDiscovery,
        Icon: SearchSolidIcon,
        contentId: ContentId.EventDiscovery,
      });
    }

    if (hasPricingV2DemandDashboard && !hasConsignorMasterUI) {
      contents.push({
        id: InventoryEventTabs.Dashboard,
        Icon: DashboardIcon,
        contentId: ContentId.Dashboard,
      });
    }

    if (
      thisEventCampaigns?.length &&
      hasSponsoredListingsV2 &&
      hasBoostTabFeature &&
      !hasConsignorMasterUI
    ) {
      contents.push({
        id: InventoryEventTabs.Boost,
        Icon: (props) => (
          <BoostIconDisplay
            {...props}
            isSelected={activeTab === InventoryEventTabs.Boost}
          />
        ),
        contentId: ContentId.Boost,
      });
    }

    if (hasPricingABTestFeature && !hasConsignorMasterUI) {
      contents.push({
        id: InventoryEventTabs.PricingABTest,
        Icon: FlashSaleIcon,
        contentId: ContentId.FlashSale,
      });
    }

    if (hasFulfillmentRulesEventLevelFeature || event?.viagId != null) {
      contents.push({
        id: InventoryEventTabs.Fulfillment,
        Icon: DeliveryIcon,
        contentId: ContentId.Delivery,
      });
    }

    if (hasAutoPricingFeature) {
      contents.push({
        id: InventoryEventTabs.SellThroughRate,
        Icon: SellThroughRateIcon,
        contentId: ContentId.SellThroughRate,
      });
    }
    if (!hasConsignorMasterUI) {
      contents.push({
        id: InventoryEventTabs.Tags,
        Icon: TagNewIcon,
        contentId: ContentId.Tags,
        useStroke: true,
      });
    }
    contents.push({
      id: InventoryEventTabs.Mobile_Sales,
      Icon: SaleIcon,
      contentId: ContentId.Sales,
      mobileOnly: true,
      hide: !hasMobileInventoryEventPageFeature,
    });
    contents.push({
      id: InventoryEventTabs.Mobile_Purchases,
      Icon: PurchaseIcon,
      contentId: ContentId.Purchases,
      mobileOnly: true,
      hide: !hasMobileInventoryEventPageFeature,
    });
    contents.push({
      id: InventoryEventTabs.Mobile_Notes,
      Icon: PlusIcon,
      contentId: ContentId.Notes,
      mobileOnly: true,
      hide: !hasMobileInventoryEventPageFeature,
    });
    return contents;
  }, [
    activeTab,
    event?.viagId,
    hasAutoPricingFeature,
    hasBoostTabFeature,
    hasConsignorMasterUI,
    hasEventDiscoveryInInventoryViewFeature,
    hasFulfillmentRulesEventLevelFeature,
    hasMobileInventoryEventPageFeature,
    hasPricingABTestFeature,
    hasPricingV2DemandDashboard,
    hasSponsoredListingsV2,
    isMobileLayout,
    thisEventCampaigns?.length,
  ]);

  return (
    <InventoryEventLocalContext.Provider
      value={{
        defaultTab: defaultInventoryTabUserSetting,
        activeTab,
        showMap,
        eventContents,
        marketplaceEventInfo,
        listings,
        compListings: compListingsQuery.data ?? [],
        toggleShowMap,
        refetchMarketplaceEventInfo,
        setActiveTab: onSetInventoryTab,
        onUpdateDefaultType: setDefaultInventoryTabUserSetting,
      }}
    >
      {children}
    </InventoryEventLocalContext.Provider>
  );
};
